import React, { memo, Fragment, useEffect, useContext, useLayoutEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Scrollbar from 'smooth-scrollbar'
import Logo from './Logo'
import VerticalNav from './VerticalNav'
import FooterNav from './FooterNav'
import SidebarProfileCard from './SidebarProfileCard'
import { SettingsContext } from '../../context/settings-context'
import { useApolloClient } from '@apollo/client'
import { AuthContext } from '../../context/auth-context'
import { ToogleSidebarIcon, ChevronRightIcon, ToogleSidebarLightIcon } from '@/components/icons/new'

const Sidebar = memo((props) => {
    const theme = localStorage.getItem('theme') || 'light'
    const client = useApolloClient()
    const navigate = useNavigate()
    const { logout } = useContext(AuthContext)
    const { sideMenuCollapsed, toggleSideMenu } = useContext(SettingsContext)

    const savedSideMenuCollapsed = () => {
        const aside = document.getElementsByTagName('aside')[0]
        if (!aside) return
        const isMini = aside.classList.contains('sidebar-mini')
        toggleSideMenu(isMini ? 'true' : 'false')
    }

    useLayoutEffect(() => {
        const barRef = document.getElementsByTagName('ASIDE')[0]
        if (barRef) {
            if (sideMenuCollapsed === 'true') {
                barRef.classList.add('sidebar-mini')
            } else {
                barRef.classList.remove('sidebar-mini')
            }

            const isMini = barRef.classList.contains('sidebar-mini')
            toggleSideMenu(isMini ? 'true' : 'false')
        }
    }, [])

    const minisidebar = (ev, setMinimize = false) => {
        // Handle case where ev might be undefined (e.g., during logout)
        if (!ev || !ev.currentTarget) {
            const asideElement = document.getElementsByTagName('ASIDE')[0]
            if (asideElement && setMinimize === true) {
                asideElement.classList.add('sidebar-mini')
                savedSideMenuCollapsed()
            }
            return
        }

        const isToggle = ev.currentTarget.classList?.contains('sidebar-toggle')
        if (isToggle) {
            const asideElement = document.getElementsByTagName('ASIDE')[0]
            if (asideElement) {
                asideElement.classList.toggle('sidebar-mini')
            }
            ev.stopPropagation()
            ev.nativeEvent.stopImmediatePropagation()
            savedSideMenuCollapsed()
            return
        }

        const asideElement = document.getElementsByTagName('ASIDE')[0]
        if (!asideElement) return

        if (setMinimize === true) {
            asideElement.classList.add('sidebar-mini')
            savedSideMenuCollapsed()
        } else if (ev.currentTarget) {
            asideElement.classList.toggle('sidebar-mini')
            savedSideMenuCollapsed()
        }
    }

    useEffect(() => {
        Scrollbar.init(document.querySelector('#my-scrollbar'))

        window.addEventListener('resize', () => {
            const tabs = document.querySelectorAll('.nav')
            const sidebarResponsive = document.querySelector('[data-sidebar="responsive"]')
            if (window.innerWidth < 1025) {
                Array.from(tabs, (elem) => {
                    if (
                        !elem.classList.contains('flex-column') &&
                        elem.classList.contains('nav-tabs') &&
                        elem.classList.contains('nav-pills')
                    ) {
                        elem.classList.add('flex-column', 'on-resize')
                    }
                    return elem.classList.add('flex-column', 'on-resize')
                })
                if (sidebarResponsive !== null) {
                    if (!sidebarResponsive.classList.contains('sidebar-mini')) {
                        sidebarResponsive.classList.add('sidebar-mini', 'on-resize')
                    }
                }
            } else {
                Array.from(tabs, (elem) => {
                    if (elem.classList.contains('on-resize')) {
                        elem.classList.remove('flex-column', 'on-resize')
                    }
                    return elem.classList.remove('flex-column', 'on-resize')
                })
                if (sidebarResponsive !== null) {
                    if (
                        sidebarResponsive.classList.contains('sidebar-mini') &&
                        sidebarResponsive.classList.contains('on-resize')
                    ) {
                        sidebarResponsive.classList.remove('sidebar-mini', 'on-resize')
                    }
                }
            }
        })
    })

    const onLogout = useCallback(
        async (expired) => {
            const options = expired
                ? {
                      state: {
                          notification: {
                              status: 'warning',
                              message: 'Your session has been expired. Please sign in again.',
                          },
                      },
                  }
                : {}

            try {
                await client.clearStore()
            } catch (error) {}

            navigate('/login', options)
            logout()
        },
        [logout, client, navigate],
    )

    return (
        <Fragment>
            <aside
                className="sidebar-white left-bordered sidebar sidebar-base tw:!border-r tw:!flex tw:flex-col tw:justify-between"
                data-sidebar="responsive"
            >
                <div className="tw:flex-1 tw:min-h-0 tw:flex tw:flex-col">
                    <div className="sidebar-header d-flex align-items-center justify-content-start">
                        <Link to="/" className="navbar-brand">
                            <Logo />
                            <h5 className="logo-title">{props.appName}</h5>
                        </Link>
                        <div
                            className="sidebar-toggle"
                            data-toggle="sidebar"
                            data-active="true"
                            onClick={minisidebar}
                            style={{ zIndex: 2000 }}
                        >
                            <button className="sidebar-toggle-button text-gray-200">
                                {theme === 'dark' ? (
                                    sideMenuCollapsed === 'true' ? (
                                        <ChevronRightIcon />
                                    ) : (
                                        <ToogleSidebarIcon />
                                    )
                                ) : (
                                    <ToogleSidebarLightIcon />
                                )}
                            </button>
                        </div>
                    </div>
                    <div className="pt-0 sidebar-body data-scrollbar tw:flex-1 tw:min-h-0" data-scroll="1" id="my-scrollbar">
                        <SidebarProfileCard />
                        <div className="sidebar-list navbar-collapse" id="sidebar">
                            <VerticalNav setToggleMini={minisidebar} />
                        </div>
                    </div>
                    <div className="sidebar-footer"></div>
                </div>

                <div className="pe-3 py-3 border-top">
                    <FooterNav onLogout={onLogout} setToggleMini={minisidebar} />
                </div>
            </aside>
        </Fragment>
    )
})

Sidebar.displayName = 'Sidebar'
export default Sidebar
